// PackageDetail: Shows all details for a single package
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Chip, Button, Divider, CircularProgress, Paper } from '@mui/material';
import api from '../services/api';

function PackageDetail() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/api/packages`)
      .then(res => {
        const found = (res.data.packages || []).find(p => p._id === id);
        if (!found) setError('Package not found');
        setPkg(found);
      })
      .catch(() => setError('Failed to fetch package'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>{error}</Box>;
  if (!pkg) return null;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 4, boxShadow: 6, bgcolor: 'background.paper', mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom color="primary.main">{pkg.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ textTransform: 'capitalize' }}>{pkg.category}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ mb: 2, fontSize: '1.15rem', color: 'text.primary' }}>{pkg.description}</Typography>
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Price</Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main">${pkg.price}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Capacity</Typography>
                <Typography variant="h6">{pkg.capacity}</Typography>
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Includes</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {(pkg.includes || []).map((inc, i) => <Chip key={i} label={inc} color="success" variant="outlined" sx={{ fontWeight: 600, fontSize: '1rem' }} />)}
              </Box>
            </Box>
            {pkg.hotels && pkg.hotels.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Hotels</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {pkg.hotels.map(h => <Chip key={h._id} label={h.name} color="info" />)}
                </Box>
              </Box>
            )}
            {pkg.eventId && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Event</Typography>
                <Typography variant="body2">{pkg.eventId.title} @ {pkg.eventId.venue} ({pkg.eventId.startDate ? new Date(pkg.eventId.startDate).toLocaleDateString() : ''}{pkg.eventId.endDate ? ' - ' + new Date(pkg.eventId.endDate).toLocaleDateString() : ''})</Typography>
              </Box>
            )}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" href="/booking" size="large">Book Now</Button>
              <Button variant="outlined" color="secondary" component={Link} to="/packages">Back to Packages</Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default PackageDetail;
